import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { CollectionOptionType } from '@/hooks/use-collection-list-options';
import { gql, useMutation } from '@apollo/client';
import { ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT } from '@/contexts/items-context-queries';
import {
  OrderGuide_RenameCollectionMutation,
  OrderGuide_RenameCollectionMutationVariables,
} from '@/types';
import { OrderGuideActionSheetNameInput } from './order-guide-action-sheet-name-input';
import { useOrderGuideStoreApi } from '@/stores/order-guide-store';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { validateCollectionName } from '@/utils/order-guide';

export const RENAME_COLLECTION_MUTATION = gql`
  mutation OrderGuide_RenameCollection(
    $collectionName: String!
    $collectionUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    updateOrderGuideCollection(
      collection_id: $collectionUUID
      name: $collectionName
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      order_guide_restaurant_collection {
        ...ItemsContext_OrderGuideRestaurantCollectionFields
      }
    }
  }

  ${ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT}
`;

interface Props {
  collections: CollectionOptionType[];
  onSave: (newName: string) => void;
  selectedCollection: CollectionOptionType;
}

export const RenameCollectionContent: React.FC<Props> = ({
  selectedCollection,
  collections,
  onSave,
}) => {
  const [renameCollection, { loading }] = useMutation<
    OrderGuide_RenameCollectionMutation,
    OrderGuide_RenameCollectionMutationVariables
  >(RENAME_COLLECTION_MUTATION);
  const [inputError, setInputError] = useState('');
  const orderGuideStoreApi = useOrderGuideStoreApi();
  const { showErrorToast } = useToasts();
  const { selectedRestaurantUUID, selectedRestaurantRelationship } =
    useUserContext();

  return (
    <OrderGuideActionSheetNameInput
      initialName={selectedCollection.title}
      inputError={inputError}
      isSaving={loading}
      nameInputLabel={i18n.t('order_guide_name')}
      onSave={async (name) => {
        try {
          const otherCollectionNames = collections.reduce<string[]>(
            (acc, collection) => {
              if (collection.value !== selectedCollection.value) {
                acc.push(collection.title);
              }
              return acc;
            },
            [],
          );

          if (!validateCollectionName(name, otherCollectionNames)) {
            setInputError(i18n.t('order_guide_name_error'));
            return;
          }

          const result = await renameCollection({
            variables: {
              collectionName: name,
              collectionUUID: String(selectedCollection.value),
              restaurantUUID: selectedRestaurantUUID,
              supplierUUID: selectedRestaurantRelationship.supplierUUID,
            },
          });

          const updatedCollection =
            result.data?.updateOrderGuideCollection
              .order_guide_restaurant_collection;
          if (updatedCollection) {
            orderGuideStoreApi
              .getState()
              .updateRestaurantCollection(
                String(selectedCollection.value),
                updatedCollection,
              );
          }

          onSave(name);
        } catch (e) {
          showErrorToast();
        }
      }}
    />
  );
};
